import React, { useState, useRef, useEffect } from 'react';
import {
  IconChatbot,
  IconInfo,
  IconSend,
  IconUser,
} from '../components/icons';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/Card';
import Button from '../components/Button';

// Demo-mode keyword responses for common health topics
const getResponse = (message) => {
  const lower = message.toLowerCase();

  if (lower.includes('preeclampsia') || lower.includes('preeclampsia')) {
    return 'Preeclampsia is a pregnancy complication characterized by high blood pressure. Key warning signs: severe headaches, vision changes, upper abdominal pain, rapid weight gain, and swelling. If you experience these symptoms, seek immediate medical care. It typically occurs after 20 weeks of pregnancy.';
  }
  if (lower.includes('malaria')) {
    return 'Malaria symptoms include fever, chills, headache, nausea, and fatigue. Malaria is preventable — use insecticide-treated bed nets, wear protective clothing, and take prescribed antimalarial medication if recommended by your doctor. Seek immediate care if you have fever and suspect malaria.';
  }
  if (lower.includes('mental health') || lower.includes('depression') || lower.includes('anxiety')) {
    return 'Mental health is as important as physical health. Common signs of concern include persistent sadness, loss of interest, sleep changes, and difficulty concentrating. You are not alone — speak to a trusted person, your doctor, or a mental health professional. It\'s okay to ask for help.';
  }
  if (lower.includes('autism') || lower.includes('asd')) {
    return 'Autism Spectrum Disorder (ASD) is a developmental condition affecting communication and behavior. Early signs in children include delayed speech, limited eye contact, repetitive behaviors, and difficulty with social interaction. Early diagnosis and intervention significantly improve outcomes. Consult a pediatrician if you have concerns.';
  }
  if (lower.includes('breast cancer')) {
    return 'Breast cancer awareness: Perform regular self-examinations monthly. See a doctor immediately if you notice a lump, change in breast shape, nipple discharge, or skin dimpling. Regular mammograms are recommended for women over 40. Early detection dramatically improves survival rates.';
  }
  if (lower.includes('diabetes') || lower.includes('blood sugar')) {
    return 'Diabetes management involves monitoring blood glucose levels, taking medications as prescribed, eating a balanced diet, exercising regularly, and attending regular check-ups. Warning signs of high blood sugar: excessive thirst, frequent urination, blurred vision, and fatigue. Contact your healthcare provider if symptoms are severe.';
  }
  if (lower.includes('hiv') || lower.includes('aids')) {
    return 'HIV is preventable and manageable with modern treatment. Prevention: use condoms consistently, get tested regularly, and if HIV-positive, antiretroviral therapy (ART) can keep you healthy and prevent transmission. Early testing and treatment are key. Visit a clinic to get tested or learn more.';
  }
  if (lower.includes('hypertension') || lower.includes('high blood pressure')) {
    return 'Hypertension (high blood pressure) often has no symptoms, which is why it\'s called the "silent killer." Control it through: a low-salt diet, regular exercise, maintaining healthy weight, limiting alcohol, and taking prescribed medications. Monitor your blood pressure regularly.';
  }
  if (lower.includes('hello') || lower.includes('hi') || lower.includes('hey')) {
    return 'Hello! I\'m the CareArena health education assistant (Demo Mode). I can answer questions about common health conditions including preeclampsia, malaria, mental health, autism, breast cancer, diabetes, HIV, and hypertension. What health topic can I help you with today?';
  }
  if (lower.includes('help') || lower.includes('what can you do')) {
    return 'I can provide health education information on: Preeclampsia, Malaria, Mental Health, Autism, Breast Cancer, Diabetes, HIV/AIDS, and Hypertension. Just ask me about any of these topics! For medical emergencies, please contact your nearest healthcare facility.';
  }

  return 'Thank you for your question. This Demo Mode assistant covers: preeclampsia, malaria, mental health, autism, breast cancer, diabetes, HIV, and hypertension. For personalized medical advice, please consult a qualified healthcare professional. Type "help" to see available topics.';
};

const Chatbot = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content:
        'Hello! I\'m the CareArena health education assistant. I can answer questions about common health conditions in Ghana. What health topic can I help you with today?',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Simulate response delay for natural UX
    await new Promise((resolve) => setTimeout(resolve, 600));

    const responseText = getResponse(text);
    const assistantMessage = {
      id: Date.now() + 1,
      role: 'assistant',
      content: responseText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, assistantMessage]);
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const topics = [
    'Preeclampsia symptoms',
    'Malaria prevention',
    'Mental health support',
    'Diabetes management',
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">AI Health Chatbot</h2>
          <p className="text-gray-600 mt-1">
            Ask questions about common health conditions and prevention
          </p>
        </div>
        {/* Demo Mode badge */}
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium">
          <IconInfo className="w-3.5 h-3.5" />
          Demo Mode
        </span>
      </div>

      {/* Demo mode notice */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <IconInfo className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Demo Mode — keyword-based responses</p>
          <p className="mt-0.5 text-amber-700">
            This chatbot uses local pattern matching for health education. Connect to the backend AI
            service (WhatsApp webhook) for full conversational AI capabilities.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat Window */}
        <div className="lg:col-span-3">
          <Card className="flex flex-col h-[600px]">
            <CardHeader className="flex-shrink-0 pb-3">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <IconChatbot className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <CardTitle className="text-base">CareArena Health Assistant</CardTitle>
                  <CardDescription className="text-xs">
                    Health education · Demo Mode
                  </CardDescription>
                </div>
                <span className="ml-auto flex items-center gap-1 text-xs text-green-600">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  Active
                </span>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col flex-1 min-h-0 p-0">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex items-start space-x-3 ${
                      message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                    }`}
                  >
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.role === 'assistant'
                          ? 'bg-primary-100'
                          : 'bg-gray-200'
                      }`}
                    >
                      {message.role === 'assistant' ? (
                        <IconChatbot className="h-4 w-4 text-primary-600" />
                      ) : (
                        <IconUser className="h-4 w-4 text-gray-600" />
                      )}
                    </div>
                    <div
                      className={`max-w-[75%] rounded-lg px-4 py-3 text-sm ${
                        message.role === 'assistant'
                          ? 'bg-gray-100 text-gray-900'
                          : 'bg-primary-600 text-white'
                      }`}
                    >
                      <p className="leading-relaxed">{message.content}</p>
                      <p
                        className={`text-xs mt-1.5 ${
                          message.role === 'assistant' ? 'text-gray-400' : 'text-primary-200'
                        }`}
                      >
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex items-start space-x-3">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <IconChatbot className="h-4 w-4 text-primary-600" />
                    </div>
                    <div className="bg-gray-100 rounded-lg px-4 py-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4">
                <div className="flex items-end space-x-3">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about a health topic (e.g. 'What are symptoms of malaria?')"
                    rows={1}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={loading}
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!input.trim() || loading}
                    loading={loading}
                    variant="primary"
                    className="flex-shrink-0"
                  >
                    <IconSend className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">Press Enter to send · Shift+Enter for new line</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar: Quick Topics */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Topics</CardTitle>
              <CardDescription className="text-xs">Click to ask</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {topics.map((topic) => (
                <button
                  key={topic}
                  onClick={() => {
                    setInput(topic);
                  }}
                  className="w-full text-left px-3 py-2 text-sm rounded-lg bg-gray-50 hover:bg-primary-50 hover:text-primary-700 transition-colors border border-gray-200 hover:border-primary-200"
                >
                  {topic}
                </button>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Covered Topics</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5 text-sm text-gray-600">
                {[
                  'Preeclampsia',
                  'Malaria',
                  'Mental Health',
                  'Autism (ASD)',
                  'Breast Cancer',
                  'Diabetes',
                  'HIV/AIDS',
                  'Hypertension',
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
